import { useEffect, useState } from "react";
import type { Compared } from "../shared/seeds";
import { VOICE_NAMES } from "../shared/voices";
import { api, ApiError } from "./api";
import { OUTCOME_LABEL } from "./common";
import { roman } from "../shared/plates";
import { Sheet } from "./trust";

/**
 * Pass it on (co-creation step 3): a story's opening, given to someone by a private link, and the endings of everyone
 * who played it, side by side. Never a line of anyone's story: the opening, its truths, and the chapter cards.
 */

const linkOf = (code: string) => `${location.origin}/play?seed=${code}`;
const dateOf = (at: number) => new Date(at).toLocaleDateString(undefined, { month: "short", day: "numeric" });

/** The sheet: what will be passed on (editable until the link exists), the link, and the endings so far. */
export function PassOn({ id, onClose }: { id: string; onClose(): void }) {
  const [code, setCode] = useState<string | null>(null);
  const [opening, setOpening] = useState("");
  const [truths, setTruths] = useState<string[]>([]);
  const [compare, setCompare] = useState<Compared[] | null>(null);
  const [state, setState] = useState<"loading" | "idle" | "making" | "refused" | "failed" | "copied">("loading");
  useEffect(() => {
    api.seed(id).then((r) => { setCode(r.code); setOpening(r.opening); setTruths(r.truths); setCompare(r.compare); setState("idle"); }, () => setState("failed"));
  }, [id]);
  const make = async () => {
    setState("making");
    try { setCode((await api.passOn(id, opening.trim(), truths.map((t) => t.trim()).filter(Boolean))).code); setState("idle"); }
    catch (e) { setState(e instanceof ApiError && e.code === "not_shareable" ? "refused" : "failed"); }
  };
  const copy = async () => {
    if (!code) return;
    try { await navigator.clipboard.writeText(linkOf(code)); setState("copied"); } catch { setState("failed"); }
  };
  const share = async () => {
    if (!code) return;
    try { await navigator.share({ title: "An opening for Weftwake", text: opening, url: linkOf(code) }); } catch { /* closed or not supported */ }
  };
  return (
    <Sheet label="Pass this opening on" onClose={onClose}>
      <div className="lm-passon">
        <h2 className="lm-sheet-h">Pass this opening on</h2>
        {state === "loading" ? <p className="lm-map-meta">…</p> : code ? (
          <>
            <p>Anyone with this link can begin their own story from your opening. It isn't listed anywhere.</p>
            <p className="lm-passon-link"><code>{linkOf(code)}</code></p>
            <div className="lm-sheet-actions">
              {"share" in navigator && <button type="button" onClick={() => void share()}>Share…</button>}
              <button type="button" onClick={() => void copy()}>{state === "copied" ? "Copied" : "Copy the link"}</button>
            </div>
          </>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); void make(); }}>
            <p>Give your opening to someone: they begin their own story from the same first line and the same truths, with the same narrator. They never see a word of your story, and you never see theirs.</p>
            <label htmlFor="lm-passon-line">The first line</label>
            <textarea id="lm-passon-line" rows={3} maxLength={400} value={opening} onChange={(e) => setOpening(e.target.value)} />
            {truths.length > 0 && <p className="lm-map-meta">True here (clear one to leave it out)</p>}
            {truths.map((t, i) => (
              <input key={i} type="text" maxLength={120} value={t} aria-label={`True here ${i + 1}`}
                onChange={(e) => setTruths((cur) => cur.map((x, k) => (k === i ? e.target.value : x)))} />
            ))}
            <p className="lm-gate-small">Your fear, if you set one, stays yours. Each chapter you finish shows them its card (the question, how it ended, the price), and theirs show here.</p>
            {state === "refused" && <p role="alert">This can't be passed on as it is: it may name a real person, or say something we wouldn't show someone else. Change it and try again.</p>}
            {state === "failed" && <p role="alert">Couldn't make the link. Check your connection and try again.</p>}
            <div className="lm-sheet-actions">
              <button type="button" onClick={onClose}>Not now</button>
              <button type="submit" disabled={!opening.trim() || state === "making"}>{state === "making" ? "Checking…" : "Make the link"}</button>
            </div>
          </form>
        )}
        {compare && compare.length > 0 && <SameOpening list={compare} />}
        {code && <div className="lm-sheet-actions"><button type="button" onClick={onClose}>Close</button></div>}
      </div>
    </Sheet>
  );
}

/** Other players' endings from the same opening: chapter by chapter, the question, how it ended, the price. */
export function SameOpening({ list }: { list: Compared[] }) {
  return (
    <section className="lm-same" aria-label="From the same opening">
      <h3>From the same opening</h3>
      {list.map((p, i) => (
        <div key={i} className="lm-same-run">
          <p className="lm-map-meta">{p.giver ? "Who passed it to you" : `Someone you passed it to · began ${dateOf(p.startedAt)}`}</p>
          {p.cards.length ? (
            <ol>
              {p.cards.map((c) => (
                <li key={c.n}>
                  <span className="lm-map-meta">Chapter {roman(c.n)} · {OUTCOME_LABEL[c.outcome]}</span> <span className="lm-same-q">{c.question}</span>
                  {c.price && c.outcome !== "earned" && <span className="lm-same-price">{c.outcome === "lost" ? "Lost: " : "The price: "}{c.price}</span>}
                </li>
              ))}
            </ol>
          ) : <p className="lm-gate-small">No chapter finished yet.</p>}
          {p.ended && <p className="lm-map-meta">The end · {OUTCOME_LABEL[p.ended]}</p>}
        </div>
      ))}
    </section>
  );
}

/** The journal's Chapters tab: the same list, fetched when the story came from a seed or was passed on. */
export function SameOpeningFor({ id }: { id: string }) {
  const [list, setList] = useState<Compared[] | null>(null);
  useEffect(() => { api.seed(id).then((r) => setList(r.compare), () => setList(null)); }, [id]);
  if (!list) return null;
  return list.length ? <SameOpening list={list} /> : <p className="lm-gate-small">No one has finished a chapter from this opening yet. Their chapter cards will show here.</p>;
}

type Welcome = { code: string; opening: string; truths: string[]; voice: string; mine: boolean };

/** An opening someone passed on, at /play?seed=…: what it is, and Begin. Nothing about who passed it on. */
export function SeedWelcome({ code, onBegin, onClose }: { code: string; onBegin(seed: Welcome): void; onClose(): void }) {
  const [seed, setSeed] = useState<Welcome | null>(null);
  const [problem, setProblem] = useState<"gone" | "mature" | "failed" | null>(null);
  const [reported, setReported] = useState(false);
  useEffect(() => {
    api.seedOf(code).then((r) => setSeed(r.seed), (e) => setProblem(e instanceof ApiError ? (e.code === "seed_mature" ? "mature" : e.code === "seed_gone" ? "gone" : "failed") : "failed"));
  }, [code]);
  const report = async () => { try { await api.reportSeed(code); } catch { /* the sheet says it either way */ } setReported(true); };
  return (
    <Sheet label="An opening passed to you" onClose={onClose}>
      <div className="lm-passon">
        <h2 className="lm-sheet-h">An opening, passed to you</h2>
        {problem === "gone" && <p>This opening isn't available any more. You can begin a story of your own.</p>}
        {problem === "mature" && <p>This opening has mature themes. To play it, turn on mature themes in <a href="/account">Account settings</a> (18+), then open the link again.</p>}
        {problem === "failed" && <p role="alert">Couldn't open it. Check your connection and try again.</p>}
        {reported && <p>Thanks. It's hidden while we look at it.</p>}
        {seed && !reported && (
          <>
            <blockquote className="lm-seed-line">{seed.opening}</blockquote>
            {seed.truths.length > 0 && (
              <ul className="lm-seed-truths" aria-label="True here">{seed.truths.map((t) => <li key={t}>{t}</li>)}</ul>
            )}
            <p className="lm-map-meta">Narrator: {VOICE_NAMES[seed.voice as keyof typeof VOICE_NAMES] ?? "Inward"}</p>
            <p className="lm-gate-small">
              {seed.mine ? "This is your own opening. " : ""}Begin it and the story is yours: no one sees what you write. Whoever passed it on sees each chapter's card when you finish it (its question, how it ended, the price), and you see theirs in your journal. Beginning uses a turn, like any first line.
            </p>
            <div className="lm-sheet-actions">
              <button type="button" onClick={onClose}>Not now</button>
              <button type="button" onClick={() => onBegin(seed)}>Begin this story</button>
            </div>
            <p className="lm-gate-small"><button type="button" className="lm-linkish" onClick={() => void report()}>Report this opening</button></p>
          </>
        )}
        {(problem || reported) && <div className="lm-sheet-actions"><button type="button" onClick={onClose}>Close</button></div>}
      </div>
    </Sheet>
  );
}
